/**
 * CLI Issues Management
 *
 * Search, list, and view issues with full diagnostics from the terminal.
 */

import process from 'node:process';
import { resolveConfig, getArgValue, hasFlag } from './auth.js';
import { apiRequest, getApplicationIdArg } from './cli-helpers.js';
import { handleIssueCreate, handleIssueBump } from './issue-create.js';

/* ── Types ────────────────────────────────────────────────────────────── */

type IssueCategorySummary = {
  id: string;
  key: string;
  label: string;
  description: string | null;
  instructions: string | null;
};

type IssueAssignee = {
  id: string;
  displayName: string | null;
  primaryEmail: string | null;
};

type FailureDiagnostic = {
  category: string;
  title: string;
  analysis: string;
  recommendation: string;
  smokingGun?: { description: string; detail?: string } | null;
};

type WorkflowSourceInfo = { workflowId: string; workflowRunId: string };

type IssueOccurrence = {
  id: string;
  sourceKind?: string;
  sourceId?: string | null;
  primaryUrl: string;
  ticketUserFacingSummary: string | null;
  ticketReproSteps: string | null;
  diagnosticJson: FailureDiagnostic | null;
  consoleLogExcerpt: string | null;
  networkFailureUrl: string | null;
  networkFailureStatus: number | null;
  workflowSourceInfo?: WorkflowSourceInfo | null;
};

type IssueSummary = {
  id: string;
  title: string;
  severity: 'low' | 'medium' | 'high' | 'unknown';
  status: 'open' | 'closed' | 'not_a_bug';
  category: IssueCategorySummary;
  firstSeenAt: string;
  lastSeenAt: string;
  occurrenceCount: number;
  assignedTo: IssueAssignee | null;
  latestOccurrence: (IssueOccurrence & { workflowSourceInfo?: WorkflowSourceInfo | null }) | null;
};

type ConsoleLogEntry = {
  type: string;
  text: string;
  timestamp: number | null;
  location: string | null;
};

type NetworkRequestEntry = {
  url: string;
  method: string;
  status: number | null;
  durationMs: number | null;
};

type Pagination = {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
};

type IssueListResponse = {
  ok: boolean;
  error?: string;
  data: IssueSummary[];
  pagination: Pagination;
};

type IssueDetailResponse = {
  ok: boolean;
  error?: string;
  data: {
    issue: IssueSummary;
    occurrences: IssueOccurrence[];
    commentCount: number;
  };
};

type DiagnosticsDataResponse = {
  ok: boolean;
  data: ConsoleLogEntry[] | NetworkRequestEntry[];
};

/* ── Formatting Helpers ───────────────────────────────────────────────── */

const SEVERITY_ICONS: Record<string, string> = {
  high: '!!!',
  medium: '!!',
  low: '!',
  unknown: '?',
};

const DIAGNOSTIC_CATEGORY_LABELS: Record<string, string> = {
  software_bug: 'Software Bug',
  dependency_issue: 'Dependency Issue',
  agent_confusion: 'Agent Confusion',
  platform_error: 'Platform Error',
};

function formatStatusLabel(status: string): string {
  switch (status) {
    case 'open':
      return 'Open';
    case 'closed':
      return 'Closed';
    case 'not_a_bug':
      return 'Not a Bug';
    default:
      return status;
  }
}

function formatSeverity(severity: string): string {
  return severity.charAt(0).toUpperCase() + severity.slice(1);
}

function formatRelative(value: string | null): string {
  if (!value) return '—';
  try {
    const date = new Date(value);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return `${diffDays}d ago`;
    return date.toISOString().slice(0, 10);
  } catch {
    return value;
  }
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max - 1) + '…';
}

/* ── Markdown Formatters ──────────────────────────────────────────────── */

function formatIssueDetailMarkdown(
  issue: IssueSummary,
  consoleErrors: ConsoleLogEntry[],
  networkErrors: NetworkRequestEntry[]
): string {
  const sections: string[] = [];
  const occ = issue.latestOccurrence;

  sections.push(`# ${issue.title}`);

  const meta: string[] = [];
  meta.push(`**Status:** ${formatStatusLabel(issue.status)}`);
  meta.push(`**Severity:** ${formatSeverity(issue.severity)}`);
  meta.push(`**Category:** ${issue.category.label}`);
  sections.push(meta.join(' | '));

  if (occ?.primaryUrl) {
    sections.push(`**URL:** ${occ.primaryUrl}`);
  }

  if (occ?.workflowSourceInfo) {
    sections.push(
      `**Workflow:** ${occ.workflowSourceInfo.workflowId} | **Workflow Run:** ${occ.workflowSourceInfo.workflowRunId}`
    );
  }

  const timeParts: string[] = [];
  timeParts.push(`**First seen:** ${formatRelative(issue.firstSeenAt)}`);
  timeParts.push(`**Last seen:** ${formatRelative(issue.lastSeenAt)}`);
  timeParts.push(`**Occurrences:** ${issue.occurrenceCount}`);
  sections.push(timeParts.join(' | '));

  const summary = occ?.ticketUserFacingSummary?.trim();
  if (summary) {
    sections.push(`## Summary\n${summary}`);
  }

  const reproSteps = occ?.ticketReproSteps?.trim();
  if (reproSteps) {
    sections.push(`## Reproduction Steps\n${reproSteps}`);
  }

  const diagnostic = occ?.diagnosticJson;
  if (diagnostic) {
    const diagParts: string[] = [];
    const categoryLabel = DIAGNOSTIC_CATEGORY_LABELS[diagnostic.category] ?? diagnostic.category;
    diagParts.push('## Diagnostic Analysis');
    diagParts.push(`**Category:** ${categoryLabel}`);
    diagParts.push(`**${diagnostic.title}**`);
    diagParts.push(diagnostic.analysis);

    if (diagnostic.smokingGun) {
      const gun = diagnostic.smokingGun;
      const gunLines = [`> **Key Evidence:** ${gun.description}`];
      if (gun.detail) {
        gunLines.push(`> \`${gun.detail}\``);
      }
      diagParts.push(gunLines.join('\n'));
    }

    diagParts.push(`**Recommendation:** ${diagnostic.recommendation}`);
    sections.push(diagParts.join('\n\n'));
  }

  if (consoleErrors.length > 0) {
    const logLines = consoleErrors.map((entry) => {
      const loc = entry.location ? ` (${entry.location})` : '';
      return `[${entry.type}] ${entry.text}${loc}`;
    });
    sections.push(`## Console Errors\n\`\`\`\n${logLines.join('\n')}\n\`\`\``);
  }

  if (networkErrors.length > 0) {
    const rows = networkErrors.map((entry) => {
      const status =
        entry.status === null || entry.status === 0 ? '0 (failed)' : String(entry.status);
      const duration =
        entry.durationMs !== null && entry.durationMs !== undefined ? `${entry.durationMs}ms` : '—';
      return `| ${entry.method} | ${entry.url} | ${status} | ${duration} |`;
    });
    sections.push(
      `## Network Errors\n| Method | URL | Status | Duration |\n|--------|-----|--------|----------|\n${rows.join('\n')}`
    );
  }

  return sections.join('\n\n');
}

function formatIssueListMarkdown(data: IssueSummary[], pagination: Pagination): string {
  const sections: string[] = [];
  sections.push(
    `# Issues (page ${pagination.page} of ${pagination.totalPages}, ${pagination.totalItems} total)`
  );

  if (data.length === 0) {
    sections.push('No issues found.');
    return sections.join('\n\n');
  }

  const header = '| Severity | Title | Status | Last Seen | Occurrences |';
  const divider = '|----------|-------|--------|-----------|-------------|';
  const rows = data.map(
    (issue) =>
      `| ${issue.severity} | ${truncate(issue.title, 60)} | ${issue.status} | ${formatRelative(issue.lastSeenAt)} | ${issue.occurrenceCount} |`
  );
  sections.push([header, divider, ...rows].join('\n'));

  return sections.join('\n\n');
}

/* ── Diagnostics Fetching ─────────────────────────────────────────────── */

async function fetchDiagnostics(
  apiUrl: string,
  token: string,
  issue: IssueSummary
): Promise<{ consoleErrors: ConsoleLogEntry[]; networkErrors: NetworkRequestEntry[] }> {
  const occ = issue.latestOccurrence;
  if (!occ) return { consoleErrors: [], networkErrors: [] };

  let consoleErrors: ConsoleLogEntry[] = [];
  let networkErrors: NetworkRequestEntry[] = [];

  try {
    const [consoleRes, networkRes] = await Promise.all([
      apiRequest<DiagnosticsDataResponse>(
        apiUrl,
        token,
        'GET',
        `/v2/issues/${issue.id}/occurrences/${occ.id}/console-logs`
      ),
      apiRequest<DiagnosticsDataResponse>(
        apiUrl,
        token,
        'GET',
        `/v2/issues/${issue.id}/occurrences/${occ.id}/network-requests`
      ),
    ]);

    if (consoleRes.ok && Array.isArray(consoleRes.data)) {
      consoleErrors = (consoleRes.data as ConsoleLogEntry[]).filter((e) => e.type === 'error');
    }

    if (networkRes.ok && Array.isArray(networkRes.data)) {
      networkErrors = (networkRes.data as NetworkRequestEntry[]).filter(
        (e) => e.status === null || e.status === 0 || e.status >= 400
      );
    }
  } catch {
    // Diagnostics are best-effort; continue without them
  }

  // Fallback to legacy fields
  if (consoleErrors.length === 0 && occ.consoleLogExcerpt?.trim()) {
    consoleErrors = [
      { type: 'error', text: occ.consoleLogExcerpt.trim(), timestamp: null, location: null },
    ];
  }

  if (networkErrors.length === 0 && occ.networkFailureUrl) {
    networkErrors = [
      {
        url: occ.networkFailureUrl,
        method: 'GET',
        status: occ.networkFailureStatus,
        durationMs: null,
      },
    ];
  }

  return { consoleErrors, networkErrors };
}

/* ── Sub-command Handlers ─────────────────────────────────────────────── */

async function handleList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const jsonOutput = hasFlag(argv, '--json');
  const fullOutput = hasFlag(argv, '--full') || hasFlag(argv, '--raw');
  const markdownOutput = getArgValue(argv, '--format') === 'markdown';

  const params = new URLSearchParams();
  const search = getArgValue(argv, '--search');
  const severity = getArgValue(argv, '--severity');
  const status = getArgValue(argv, '--status');
  const propertyId = getApplicationIdArg(argv);
  const page = getArgValue(argv, '--page');
  const pageSize = getArgValue(argv, '--page-size');

  if (search) params.set('search', search);
  if (severity) params.set('severity', severity);
  if (status) params.set('statuses', status);
  if (propertyId) params.set('propertyId', propertyId);
  if (page) params.set('page', page);
  if (pageSize) params.set('pageSize', pageSize);

  const qs = params.toString();
  const path = `/v2/issues${qs ? `?${qs}` : ''}`;

  const result = await apiRequest<IssueListResponse>(apiUrl, token, 'GET', path);

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }

  if (jsonOutput) {
    if (fullOutput) {
      console.log(JSON.stringify({ data: result.data, pagination: result.pagination }, null, 2));
    } else {
      const compact = result.data.map((issue) => {
        const occ = issue.latestOccurrence;
        const source: Record<string, unknown> = {
          kind: occ?.sourceKind ?? null,
          id: occ?.sourceId ?? null,
        };
        if (occ?.workflowSourceInfo) source.workflow = occ.workflowSourceInfo;
        return {
          id: issue.id,
          title: issue.title,
          severity: issue.severity,
          status: issue.status,
          occurrenceCount: issue.occurrenceCount,
          lastSeenAt: issue.lastSeenAt,
          source,
        };
      });
      console.log(JSON.stringify({ data: compact, pagination: result.pagination }, null, 2));
    }
    return;
  }

  if (markdownOutput) {
    console.log(formatIssueListMarkdown(result.data, result.pagination));
    return;
  }

  // Default compact output
  const { data, pagination } = result;
  console.log(
    `Issues: ${pagination.totalItems} total (page ${pagination.page}/${pagination.totalPages})\n`
  );

  if (data.length === 0) {
    console.log('No issues found.');
    return;
  }

  for (const issue of data) {
    const icon = SEVERITY_ICONS[issue.severity] ?? '?';
    const statusLabel = formatStatusLabel(issue.status);
    const lastSeen = formatRelative(issue.lastSeenAt);
    const occ = issue.latestOccurrence;
    const sourceTag = occ?.workflowSourceInfo
      ? ` [wf:${occ.workflowSourceInfo.workflowRunId.slice(0, 8)}]`
      : occ?.sourceKind && occ.sourceKind !== 'workflow_test'
        ? ` [${occ.sourceKind}]`
        : '';
    console.log(
      `  [${icon}] ${truncate(issue.title, 60)}  ${statusLabel}  ${lastSeen}  (${issue.occurrenceCount}x)${sourceTag}  ${issue.id}`
    );
  }
}

function parseIncludes(argv: string[]): Set<string> {
  const raw = getArgValue(argv, '--include');
  if (!raw) return new Set();
  return new Set(
    raw
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)
  );
}

function buildCompactIssueGet(
  issue: IssueSummary,
  consoleErrors: ConsoleLogEntry[],
  networkErrors: NetworkRequestEntry[],
  includes: Set<string>
): Record<string, unknown> {
  const occ = issue.latestOccurrence;
  const diag = occ?.diagnosticJson ?? null;

  const source: Record<string, unknown> = {
    kind: occ?.sourceKind ?? null,
    id: occ?.sourceId ?? null,
  };
  if (occ?.workflowSourceInfo) source.workflow = occ.workflowSourceInfo;

  const compact: Record<string, unknown> = {
    id: issue.id,
    title: issue.title,
    status: issue.status,
    severity: issue.severity,
    category: { key: issue.category.key, label: issue.category.label },
    occurrenceCount: issue.occurrenceCount,
    firstSeenAt: issue.firstSeenAt,
    lastSeenAt: issue.lastSeenAt,
    primaryUrl: occ?.primaryUrl ?? null,
    assignedTo: issue.assignedTo
      ? { id: issue.assignedTo.id, displayName: issue.assignedTo.displayName }
      : null,
    source,
    summary: occ?.ticketUserFacingSummary ?? null,
    diagnostic: diag
      ? {
          category: diag.category,
          title: diag.title,
          recommendation: diag.recommendation,
        }
      : null,
    consoleErrorCount: consoleErrors.length,
    networkErrorCount: networkErrors.length,
    links: {
      occurrences: `/v2/issues/${issue.id}/occurrences`,
      consoleLogs: occ
        ? `/v2/issues/${issue.id}/occurrences/${occ.id}/console-logs`
        : null,
      networkRequests: occ
        ? `/v2/issues/${issue.id}/occurrences/${occ.id}/network-requests`
        : null,
      reproPrompt: `/v2/issues/${issue.id}/repro-prompt`,
    },
  };

  if (includes.has('console')) compact.consoleErrors = consoleErrors;
  if (includes.has('network')) compact.networkErrors = networkErrors;
  if (includes.has('repro')) compact.reproSteps = occ?.ticketReproSteps ?? null;
  if (includes.has('diagnostic-full')) compact.diagnosticFull = diag;

  return compact;
}

async function handleGet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const issueId = argv[0];
  if (!issueId || issueId.startsWith('--')) {
    console.error('Error: Missing issue ID.');
    console.error('Usage: canary issues get <issueId>');
    process.exit(1);
  }

  const jsonOutput = hasFlag(argv, '--json');
  const fullOutput = hasFlag(argv, '--full') || hasFlag(argv, '--raw');
  const markdownOutput = getArgValue(argv, '--format') === 'markdown';
  const includes = parseIncludes(argv);

  const result = await apiRequest<IssueDetailResponse>(
    apiUrl,
    token,
    'GET',
    `/v2/issues/${issueId}`
  );

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }

  const { issue } = result.data;

  // Diagnostics are prefetched only when the user will see them (text/markdown
  // output, `--full`, or an explicit `--include console|network`). Compact JSON
  // without those flags returns occurrence counts only and leaves the heavy
  // arrays behind the linked endpoints.
  const needDiagnostics =
    !jsonOutput ||
    fullOutput ||
    includes.has('console') ||
    includes.has('network');
  const { consoleErrors, networkErrors } = needDiagnostics
    ? await fetchDiagnostics(apiUrl, token, issue)
    : { consoleErrors: [] as ConsoleLogEntry[], networkErrors: [] as NetworkRequestEntry[] };

  if (jsonOutput) {
    if (fullOutput) {
      console.log(JSON.stringify({ ...result.data, consoleErrors, networkErrors }, null, 2));
    } else {
      console.log(
        JSON.stringify(buildCompactIssueGet(issue, consoleErrors, networkErrors, includes), null, 2)
      );
    }
    return;
  }

  if (markdownOutput) {
    console.log(formatIssueDetailMarkdown(issue, consoleErrors, networkErrors));
    return;
  }

  // Default human-readable output
  const occ = issue.latestOccurrence;
  console.log(`  Title:        ${issue.title}`);
  console.log(`  ID:           ${issue.id}`);
  console.log(`  Status:       ${formatStatusLabel(issue.status)}`);
  console.log(`  Severity:     ${formatSeverity(issue.severity)}`);
  console.log(`  Category:     ${issue.category.label}`);
  console.log(`  Occurrences:  ${issue.occurrenceCount}`);
  console.log(`  First seen:   ${formatRelative(issue.firstSeenAt)}`);
  console.log(`  Last seen:    ${formatRelative(issue.lastSeenAt)}`);

  if (occ?.primaryUrl) {
    console.log(`  URL:          ${occ.primaryUrl}`);
  }

  if (occ?.workflowSourceInfo) {
    console.log(`  Workflow:     ${occ.workflowSourceInfo.workflowId}`);
    console.log(`  Workflow Run: ${occ.workflowSourceInfo.workflowRunId}`);
  } else if (occ?.sourceKind) {
    console.log(`  Source:       ${occ.sourceKind}${occ.sourceId ? ` (${occ.sourceId})` : ''}`);
  }

  if (issue.assignedTo) {
    console.log(
      `  Assigned to:  ${issue.assignedTo.displayName ?? issue.assignedTo.primaryEmail ?? issue.assignedTo.id}`
    );
  }

  const summary = occ?.ticketUserFacingSummary?.trim();
  if (summary) {
    console.log(`\n  Summary:\n    ${summary.split('\n').join('\n    ')}`);
  }

  const diagnostic = occ?.diagnosticJson;
  if (diagnostic) {
    const categoryLabel = DIAGNOSTIC_CATEGORY_LABELS[diagnostic.category] ?? diagnostic.category;
    console.log(`\n  Diagnostic: ${categoryLabel}`);
    console.log(`    ${diagnostic.title}`);
    console.log(`    Recommendation: ${diagnostic.recommendation}`);
  }

  if (consoleErrors.length > 0) {
    console.log(`\n  Console Errors (${consoleErrors.length}):`);
    for (const entry of consoleErrors.slice(0, 5)) {
      const loc = entry.location ? ` (${entry.location})` : '';
      console.log(`    [${entry.type}] ${truncate(entry.text, 100)}${loc}`);
    }
    if (consoleErrors.length > 5) {
      console.log(`    ... and ${consoleErrors.length - 5} more`);
    }
  }

  if (networkErrors.length > 0) {
    console.log(`\n  Network Errors (${networkErrors.length}):`);
    for (const entry of networkErrors.slice(0, 5)) {
      const status = entry.status === null || entry.status === 0 ? 'failed' : String(entry.status);
      console.log(`    ${entry.method} ${truncate(entry.url, 80)} -> ${status}`);
    }
    if (networkErrors.length > 5) {
      console.log(`    ... and ${networkErrors.length - 5} more`);
    }
  }
}

type IssueStatusUpdateResponse = {
  ok: boolean;
  error?: string;
  status?: 'open' | 'closed' | 'not_a_bug';
  statusUpdatedAt?: string;
  closedAt?: string | null;
};

async function handleClose(argv: string[], apiUrl: string, token: string): Promise<void> {
  const issueId = argv[0];
  if (!issueId || issueId.startsWith('--')) {
    console.error('Error: Missing issue ID.');
    console.error('Usage: canary issues close <issueId> [--not-a-bug --reason <text>]');
    process.exit(1);
  }

  const notABug = hasFlag(argv, '--not-a-bug');
  const reason = getArgValue(argv, '--reason') ?? getArgValue(argv, '--feedback');
  const jsonOutput = hasFlag(argv, '--json');

  if (notABug && !reason?.trim()) {
    console.error('Error: --not-a-bug requires --reason <text>.');
    process.exit(1);
  }

  const status: 'closed' | 'not_a_bug' = notABug ? 'not_a_bug' : 'closed';
  const body: Record<string, unknown> = { status };
  if (reason?.trim()) body.feedback = reason.trim();

  const result = await apiRequest<IssueStatusUpdateResponse>(
    apiUrl,
    token,
    'PATCH',
    `/issues/${issueId}/status`,
    body
  );

  if (!result.ok) {
    if (jsonOutput) {
      console.log(JSON.stringify({ ok: false, error: result.error }, null, 2));
    } else {
      console.error(`Error: ${result.error}`);
    }
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  console.log(
    `Issue ${issueId} ${status === 'not_a_bug' ? 'marked not-a-bug' : 'closed'}.`
  );
}

async function handleReopen(argv: string[], apiUrl: string, token: string): Promise<void> {
  const issueId = argv[0];
  if (!issueId || issueId.startsWith('--')) {
    console.error('Error: Missing issue ID.');
    console.error('Usage: canary issues reopen <issueId>');
    process.exit(1);
  }

  const jsonOutput = hasFlag(argv, '--json');

  const result = await apiRequest<IssueStatusUpdateResponse>(
    apiUrl,
    token,
    'PATCH',
    `/issues/${issueId}/status`,
    { status: 'open' }
  );

  if (!result.ok) {
    if (jsonOutput) {
      console.log(JSON.stringify({ ok: false, error: result.error }, null, 2));
    } else {
      console.error(`Error: ${result.error}`);
    }
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  console.log(`Issue ${issueId} reopened.`);
}

/* ── Help & Entry Point ───────────────────────────────────────────────── */

function printIssuesHelp(): void {
  console.log(
    [
      'Usage: canary issues <sub-command> [options]',
      '',
      'Sub-commands:',
      '  list [options]                       List and search issues',
      '  get <issueId> [options]              Get issue detail with diagnostics',
      '  create [options]                     File a new issue (human or agent)',
      '  bump <issueId> [options]             Add a sighting to an existing issue',
      '  close <issueId> [options]            Close an issue (or mark not-a-bug)',
      '  reopen <issueId> [--json]            Reopen a closed issue',
      '',
      'Create options:',
      '  --title <text>           Issue title (required)',
      '  --application-id <uuid>  Application/property (required; alias --property-id)',
      '  --url <url>              Page where the bug lives (required unless --check)',
      '  --severity <level>       low | medium | high | unknown (default: medium)',
      '  --category <key>         Issue category key (default: business_logic_failure)',
      '  --body <text>            Freeform Markdown report (or --from-file <path>)',
      '  --from-run <runId>       Link a workflow run; attaches its fast video + trace',
      '  --code-ref <path[:line]> Cite a defect location (repeatable; --repo/--commit apply)',
      '  --sentry <id|url>        Attach a Sentry issue/trace (repeatable)',
      '  --log-query <query>      Attach a log query (repeatable; --log-system <sys>)',
      '                           Allowed log systems: sentry, cloudwatch, datadog, other',
      '                           Unsupported values are stored as other',
      '  --check                  List possible duplicates and stop (search-first)',
      '',
      'Bump options:',
      '  --url <url>              Page where the bug was seen again (required)',
      '  --from-run <runId>       Link a workflow run (attaches fast video + trace)',
      '  --body / --code-ref / --sentry / --log-query   Same as create',
      '',
      'Close options:',
      '  --not-a-bug           Mark as not-a-bug instead of closed (requires --reason)',
      '  --reason <text>       Feedback/reason (required when --not-a-bug)',
      '  --json                JSON output',
      '',
      'List options:',
      '  --search <query>      Full-text search',
      '  --severity <level>    Filter: low, medium, high, unknown',
      '  --status <statuses>   Filter: open, closed, not_a_bug (comma-separated)',
      '  --application-id <uuid>  Filter by application (alias: --property-id)',
      '  --page <n>            Page number (default: 1)',
      '  --page-size <n>       Page size (default: 25)',
      '',
      'Output options:',
      '  --json                Compact JSON (workflow IDs + counts + links)',
      '  --full                With --json, return the full unmodified response',
      '  --include <csv>       With --json on `get`: console, network, repro, diagnostic-full',
      '  --format markdown     Output as markdown',
      '',
      'Common options:',
      '  --env <env>           Target environment (prod, dev, local)',
      '  --api-url <url>       API URL override (takes precedence over --env)',
      '  --token <key>         API token override',
    ].join('\n')
  );
}

export async function runIssues(argv: string[]): Promise<void> {
  const [subCommand, ...rest] = argv;

  if (!subCommand || subCommand === 'help' || hasFlag(argv, '--help', '-h')) {
    printIssuesHelp();
    return;
  }

  const { apiUrl, token } = await resolveConfig(argv);

  switch (subCommand) {
    case 'list':
      await handleList(rest, apiUrl, token);
      break;
    case 'get':
      await handleGet(rest, apiUrl, token);
      break;
    case 'create':
      await handleIssueCreate(rest, apiUrl, token);
      break;
    case 'bump':
      await handleIssueBump(rest, apiUrl, token);
      break;
    case 'close':
      await handleClose(rest, apiUrl, token);
      break;
    case 'reopen':
      await handleReopen(rest, apiUrl, token);
      break;
    default:
      console.error(`Unknown sub-command: ${subCommand}`);
      printIssuesHelp();
      process.exit(1);
  }
}
